import "./_MultiSelect.scss";

import cx from "classnames";
import {ReactElement, useEffect, useState} from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
  ValidationRule,
} from "react-hook-form";

import {isPuriel} from "@/function/isPuriel";
import {MultiSeletcOption} from "@/ts/interface/formulaire";

import Button from "../Button/Button";

function MultiSelect({
  label = "",
  type = "checkbox",
  options,
  maxOptionSelect = 9999999,
  name,
  onClick,
  needMin1Check = false,
  disabled = false,
  nbLigne = 1,
  hasBorder = true,
  hasButtonAllSelect = true,
  uniqueKey = "multiselect",
  isRequired = false,
  control,
  setValue,
  watch,
}: {
  label?: string;
  type?: "radio" | "checkbox";
  options?: MultiSeletcOption[];
  maxOptionSelect?: number;
  name: string;

  onClick: (isCheck: boolean, value: string) => void;
  needMin1Check?: boolean;
  disabled?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  control: Control<FieldValues, any, FieldValues>;
  isRequired?: ValidationRule<boolean>;
  watch: UseFormWatch<FieldValues>;
  nbLigne?: 1 | 2 | 4;
  hasBorder?: boolean;
  hasButtonAllSelect?: boolean;
  uniqueKey: string;
}): ReactElement {
  const watchValues = watch(name) || []; // Observe les valeurs sélectionnées
  const allIsChecked =
    watchValues.length >=
    (options?.filter(option => option.isDisabled !== true).length || 0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleSelectAll = (): void => {
    if (
      options?.length &&
      watchValues.length === options.length &&
      options.length >= maxOptionSelect
    ) {
      return;
    } else {
      setValue(
        name,
        options
          ?.filter(option => option.isDisabled !== true)
          .map(option => {
            return {
              libelle: option.libelle,
              value: option.value,
              complementData: option?.complementData,
            };
          }), // Tout cocher
      );
    }
  };
  const handleDeleteAll = (): void => {
    setValue(name, []); // Tout décocher
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: MultiSeletcOption,
    field: ControllerRenderProps<FieldValues, string>,
  ): void => {
    let newValue = [];

    if (e.target.checked) {
      // permet de verifier que le nombre d'élément ne dépasse pas la limite.
      if (Array.isArray(field.value) && field.value.length >= maxOptionSelect) {
        e.preventDefault();
        setErrorMessage(
          `Vous ne pouvez pas cochez plus de ${maxOptionSelect} element${isPuriel(maxOptionSelect)}`,
        );
        return;
      }

      newValue = [
        ...field.value,
        {
          libelle: option.libelle,
          value: option.value,
          complementData: option?.complementData,
        },
      ];
    } else {
      newValue = field?.value?.filter(
        (val: MultiSeletcOption) => val.value !== option.value,
      ); // Supprime du tableau
    }

    if (newValue.length === 0 && needMin1Check) {
      setErrorMessage("Vous devez au moins cocher 1 élément");
      return;
    } // reset le message d'erreur
    setErrorMessage(null);
    onClick(e.target.checked, option.value.toString()); // Appelle la fonction onClick

    setValue(name, newValue); // Met à jour la valeur RHF
  };
  const allOptions: ReactElement[] | undefined = options?.map(
    (option, index) => {
      return (
        <div
          className={cx(
            "multiSelect-checkbox",
            hasBorder ? "multiSelect-border" : "",
            option.isDisabled ? "disabled" : "",
          )}
          key={`${uniqueKey}${index}`}>
          <Controller
            key={`${uniqueKey}${index}`}
            control={control}
            name={name}
            rules={{required: isRequired}}
            defaultValue={options
              .filter(
                (option, index) =>
                  option.defaultChecked || (needMin1Check && index === 0),
              )
              .map(option => {
                return {libelle: option.libelle, value: option.value};
              })}
            render={({field, fieldState}) => {
              useEffect(() => {
                setErrorMessage(fieldState.error?.message || null);
              }, [fieldState.error]);
              // Si l'element est checked par defaut, récupérer le compelementData
              useEffect(() => {
                if (Array.isArray(field.value)) {
                  const fieldIndex = field.value.findIndex(
                    (val: MultiSeletcOption) => val.value === option.value,
                  );
                  if (fieldIndex >= 0) {
                    field.value[fieldIndex].complementData =
                      option.complementData;
                  }
                }
              }, []);
              return (
                <input
                  type={type}
                  value={option.value}
                  checked={
                    Array.isArray(field.value) &&
                    field.value.findIndex(
                      (val: MultiSeletcOption) => val.value === option.value,
                    ) > -1
                  }
                  disabled={disabled || option.isDisabled}
                  name={name}
                  onChange={e => handleChange(e, option, field)}
                  id={`option_${uniqueKey} ${index}`}
                />
              );
            }}
          />
          <label htmlFor={`option_${uniqueKey} ${index}`}>
            <p
              className={cx(
                option?.tooltip ? "multiSelect-checkbox--label" : "",
              )}>
              {option.libelle}
            </p>
            {option?.tooltip ? (
              <p className="multiSelect-tooltip"> {option.tooltip}</p>
            ) : null}
            <span>
              {option?.complementLibelle ? ` ${option.complementLibelle}` : ""}
            </span>
          </label>
        </div>
      );
    },
  );
  return (
    <div className={cx("multiSelect", disabled ? "disabled" : "")}>
      <div className={"multiSelect-header"}>
        <p>
          {label}
          {maxOptionSelect != 9999999 ? (
            <span> {`(max : ${maxOptionSelect})`}</span>
          ) : null}
        </p>
        {hasButtonAllSelect ? (
          <Button
            label={
              !needMin1Check && allIsChecked ? "Supprimer tous" : "Ajouter tous"
            }
            variant={"secondary"}
            onClick={() => {
              if (disabled) {
                return;
              }
              if (!needMin1Check && allIsChecked) {
                handleDeleteAll();
              } else {
                handleSelectAll();
              }
            }}
          />
        ) : null}
      </div>
      <div className={`multiSelect-${nbLigne}Column`}>
        {errorMessage && <p className="form-error">{errorMessage}</p>}
        {allOptions}
      </div>
    </div>
  );
}

export default MultiSelect;
